// ============================================================================
// FIXED AUTH SERVICE - PIN LOGIN WITH PASSWORD SYNC
// frontend/src/services/auth.service.ts
// ============================================================================
//
// FIX: Added Accounting Manager support for PIN-based login
// ============================================================================

import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  User,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  updateDoc,
  setDoc,
  Timestamp
} from 'firebase/firestore';
import { auth, db } from './firebase';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  userType: 'admin' | 'engineer' | 'employee' | 'approver' | 'hr_manager' | 'assistant_manager' | 'accounting_manager';
  isAdmin?: boolean;
  isEngineer?: boolean;
  isApprover?: boolean;
  isHrManager?: boolean;
  isAssistantManager?: boolean;
  isAccountingManager?: boolean;
  isPinAuth?: boolean;
  name?: string;
  designation?: string;
  department?: string;
  employeeNumber?: string;
  employeeNo?: string;
  employeeName?: string;
  firstName?: string;
  lastName?: string;
  gender?: string;
  nationality?: string;
  mobileNo?: string;
  workEmail?: string;
  workTiming?: string;
  salary?: number;
  hiringDate?: string;
  dateOfBirth?: string;
  emiratesIdNo?: string;
  emiratesIdExpiryDate?: string;
  residencyVisaExpiryDate?: string;
  residencyVisaFileNumber?: string;
  residencyVisaUID?: string;
  passportNumber?: string;
  passportExpiry?: string;
  lineManagerName?: string;
  lineManagerId?: string;
  reportsTo?: string;
  title?: string;
  preferredName?: string;
  legalCountryOfResidence?: string;
  probationEndDate?: string;
  phone?: string;
  workNo?: string;
  endTime?: string;
  startTime?: string;
  previousTiming?: string;
  workWeek?: string;
  currentlyInsured?: boolean;
  sendInvitation?: boolean;
  hasOvertime?: boolean;
  overtime?: string;
  overtimeUpdatedAt?: any;
  overtimeAccessGrantedAt?: any;
  overtimeAccessRevokedAt?: any;
  pinNumber?: string;
  pinExpiry?: any;
  pinCreatedAt?: any;
  manpowerAccess?: boolean;
  leaveApplicationAccess?: boolean;
  permissions?: string[];
  hasOvertimeAccess?: boolean;
  hasOvertimeApprovalAccess?: boolean;
  createdAt?: any;
  updatedAt?: any;
  [key: string]: any;
}

class AuthService {
  private _userDataCache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly PIN_EXPIRY_DAYS = 90;
  
  // PIN password generation - consistent format
  private generatePinPassword(employeeNumber: string, pin: string): string {
    return `PTS_PIN_${employeeNumber}_${pin}_AUTH`;
  }
  
  // PIN email generation - consistent format
  private generatePinEmail(employeeNumber: string): string {
    return `emp${employeeNumber}@ptspin.local`;
  }

  // ============================================================================
  // AUTHENTICATION METHODS
  // ============================================================================

  async setAuthPersistence(remember: boolean): Promise<void> {
    try {
      if (remember) {
        await setPersistence(auth, browserLocalPersistence);
      } else {
        await setPersistence(auth, browserSessionPersistence);
      }
    } catch (error) {
      console.error('Error setting persistence:', error);
      throw error;
    }
  }

  async login(email: string, password: string): Promise<AuthUser> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const userData = await this.fetchUserData(user.uid, email);
      
      return this.buildAuthUser(user, userData);
    } catch (error: any) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  /**
   * PIN-based login - FIXED VERSION WITH ACCOUNTING MANAGER SUPPORT
   * 
   * Flow:
   * 1. Query MasterSheet to find employee and validate PIN
   * 2. Check pinAuthUsers for existing auth email
   * 3. Try to sign in with stored auth email
   * 4. If user doesn't exist or password wrong, create new user
   * 5. Return authenticated user with full data
   */
  async loginWithPin(employeeNumber: string, pin: string): Promise<AuthUser> {
    try {
      // Validate PIN format (4-6 digits)
      if (!this.isValidPin(pin)) {
        throw new Error('Invalid PIN format. PIN must be 4-6 digits.');
      }

      // Step 1: Query MasterSheet to find employee
      const employeeQuery = query(
        collection(db, 'MasterSheet', 'Employee-Data', 'employees'),
        where('employeeNumber', '==', employeeNumber)
      );
      
      const employeeSnapshot = await getDocs(employeeQuery);
      
      if (employeeSnapshot.empty) {
        throw new Error('Employee not found');
      }

      const employeeDoc = employeeSnapshot.docs[0];
      const employeeData = employeeDoc.data();
      
      // Step 2: Check if PIN exists and is valid
      if (!employeeData.pinNumber) {
        throw new Error('PIN not set for this employee. Please contact HR.');
      }

      // Step 3: Verify PIN against MasterSheet
      if (employeeData.pinNumber !== pin) {
        throw new Error('Invalid PIN');
      }

      // Step 4: Check if PIN is expired
      if (employeeData.pinExpiry) {
        let expiryDate: Date;
        if (employeeData.pinExpiry.toDate) {
          expiryDate = employeeData.pinExpiry.toDate();
        } else if (employeeData.pinExpiry.seconds) {
          expiryDate = new Date(employeeData.pinExpiry.seconds * 1000);
        } else {
          expiryDate = new Date(employeeData.pinExpiry);
        }
        
        if (new Date() > expiryDate) {
          throw new Error('PIN has expired. Please contact HR to reset.');
        }
      }

      // Step 5: Check if we have existing auth credentials stored
      const pinAuthDocRef = doc(db, 'pinAuthUsers', employeeNumber);
      const pinAuthDoc = await getDoc(pinAuthDocRef);
      
      let authEmail: string;
      let authPassword: string = this.generatePinPassword(employeeNumber, pin);
      let user: User;

      if (pinAuthDoc.exists()) {
        // Use stored email
        const pinAuthData = pinAuthDoc.data();
        authEmail = pinAuthData.authEmail;
        
        try {
          // Try to sign in with stored email
          const userCredential = await signInWithEmailAndPassword(auth, authEmail, authPassword);
          user = userCredential.user;
          console.log('PIN login successful for employee:', employeeNumber);
          
        } catch (signInError: any) {
          console.log('Sign in error code:', signInError.code);
          
          if (signInError.code === 'auth/wrong-password' || 
              signInError.code === 'auth/invalid-credential' ||
              signInError.code === 'auth/user-not-found') {
            // PIN was changed or user deleted - create new auth user
            console.log('Creating new auth user due to PIN change for employee:', employeeNumber);
            
            // Generate new unique email
            const newAuthEmail = `emp${employeeNumber}_${Date.now()}@ptspin.local`;
            
            try {
              const createCredential = await createUserWithEmailAndPassword(auth, newAuthEmail, authPassword);
              user = createCredential.user;
              
              // Update pinAuthUsers with new email
              await setDoc(pinAuthDocRef, {
                employeeNumber: employeeNumber,
                authEmail: newAuthEmail,
                authUid: user.uid,
                employeeName: employeeData.employeeName,
                designation: employeeData.designation,
                pinUpdatedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
              }, { merge: true });
              
              console.log('Successfully created new auth user for employee:', employeeNumber);
              
            } catch (createError: any) {
              console.error('Error creating new PIN user:', createError);
              throw new Error('Authentication failed. Please contact HR.');
            }
          } else if (signInError.code === 'auth/too-many-requests') {
            throw new Error('Too many login attempts. Please wait a few minutes and try again.');
          } else {
            throw new Error('Authentication failed. Please try again.');
          }
        }
      } else {
        // No existing auth - create new user
        authEmail = this.generatePinEmail(employeeNumber);
        
        try {
          // Try to sign in first (in case user exists but not in pinAuthUsers)
          const userCredential = await signInWithEmailAndPassword(auth, authEmail, authPassword);
          user = userCredential.user;
          
          // Store reference
          await setDoc(pinAuthDocRef, {
            employeeNumber: employeeNumber,
            authEmail: authEmail,
            authUid: user.uid,
            employeeName: employeeData.employeeName,
            designation: employeeData.designation,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          });
          
        } catch (signInError: any) {
          if (signInError.code === 'auth/user-not-found') {
            // Create new user
            console.log('Creating new PIN auth user for employee:', employeeNumber);
            
            try {
              const createCredential = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
              user = createCredential.user;
              
              // Store reference
              await setDoc(pinAuthDocRef, {
                employeeNumber: employeeNumber,
                authEmail: authEmail,
                authUid: user.uid,
                employeeName: employeeData.employeeName,
                designation: employeeData.designation,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
              });
              
            } catch (createError: any) {
              if (createError.code === 'auth/email-already-in-use') {
                // Email exists but with different password - create with timestamp
                const newAuthEmail = `emp${employeeNumber}_${Date.now()}@ptspin.local`;
                const createCredential = await createUserWithEmailAndPassword(auth, newAuthEmail, authPassword);
                user = createCredential.user;
                
                await setDoc(pinAuthDocRef, {
                  employeeNumber: employeeNumber,
                  authEmail: newAuthEmail,
                  authUid: user.uid,
                  employeeName: employeeData.employeeName,
                  designation: employeeData.designation,
                  createdAt: Timestamp.now(),
                  updatedAt: Timestamp.now()
                });
              } else {
                throw createError;
              }
            }
          } else if (signInError.code === 'auth/wrong-password' || 
                     signInError.code === 'auth/invalid-credential') {
            // Password wrong - create with timestamp
            const newAuthEmail = `emp${employeeNumber}_${Date.now()}@ptspin.local`;
            const createCredential = await createUserWithEmailAndPassword(auth, newAuthEmail, authPassword);
            user = createCredential.user;
            
            await setDoc(pinAuthDocRef, {
              employeeNumber: employeeNumber,
              authEmail: newAuthEmail,
              authUid: user.uid,
              employeeName: employeeData.employeeName,
              designation: employeeData.designation,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            });
          } else {
            throw signInError;
          }
        }
      }

      // Step 7: Build the user data with proper permissions - FIXED ACCOUNTING MANAGER DETECTION
      const isHrManager = 
        employeeData.designation?.toLowerCase().includes('hr manager') ||
        employeeData.designation?.toLowerCase().includes('hr & office manager') ||
        employeeData.title?.toLowerCase().includes('hr manager') ||
        employeeData.title?.toLowerCase().includes('hr & office manager') ||
        employeeData.isHrManager === true;
      
      const isAssistantManager = 
        employeeData.designation?.toLowerCase().includes('assistant manager') ||
        employeeData.title?.toLowerCase().includes('assistant manager') ||
        employeeData.isAssistantManager === true;
      
      // FIX: Added Accounting Manager detection
      const isAccountingManager = 
        employeeData.designation?.toLowerCase().includes('accounting manager') ||
        employeeData.title?.toLowerCase().includes('accounting manager') ||
        employeeData.isAccountingManager === true;
      
      const hasApprovalAccess = employeeData.hasOvertimeApprovalAccess === true;

      let userType: AuthUser['userType'] = 'employee';
      if (isHrManager) userType = 'hr_manager';
      else if (isAssistantManager) userType = 'assistant_manager';
      else if (isAccountingManager) userType = 'accounting_manager'; // FIX: Added accounting_manager
      else if (hasApprovalAccess) userType = 'approver';

      // Build permissions array based on role - FIX: Added Accounting Manager permissions
      const permissions: string[] = ['self-service'];
      
      // HR and Assistant Managers get HR permissions
      if (isHrManager || isAssistantManager) {
        permissions.push(
          'hr_leave_approval', 
          'hr_payroll_access', 
          'attendance_access', 
          'view_reports',
          'overtime_reports'
        );
      }
      
      // FIX: Accounting Manager gets finance-related permissions
      if (isAccountingManager) {
        permissions.push(
          'finance_access',
          'payroll_access',
          'view_reports',
          'overtime_reports',
          'salary_reports'
        );
      }
      
      if (hasApprovalAccess) {
        permissions.push('overtime_approval');
      }

      // FIX: Determine leave application access for Accounting Manager
      const leaveApplicationAccess = 
        employeeData.leaveApplicationAccess === true || 
        isHrManager || 
        isAssistantManager ||
        isAccountingManager; // FIX: Accounting Managers also get leave access

      return {
        uid: user.uid,
        email: user.email,
        displayName: employeeData.employeeName || null,
        userType: userType,
        isAdmin: false,
        isEngineer: false,
        isApprover: hasApprovalAccess,
        isHrManager: isHrManager,
        isAssistantManager: isAssistantManager,
        isAccountingManager: isAccountingManager, // FIX: Added isAccountingManager
        isPinAuth: true,
        // Employee Details
        name: employeeData.employeeName,
        employeeName: employeeData.employeeName,
        firstName: employeeData.firstName || employeeData['First Name*'],
        lastName: employeeData.lastName || employeeData['Last Name*'],
        designation: employeeData.designation || employeeData.title,
        department: this.getDepartmentFromDesignation(employeeData.designation || employeeData.title),
        employeeNumber: employeeData.employeeNumber,
        employeeNo: employeeData.employeeNo || employeeDoc.id,
        gender: employeeData.gender || employeeData.Gender,
        nationality: employeeData.nationality || employeeData.Nationality,
        mobileNo: employeeData.mobileNo || employeeData['Mobile No.'],
        workEmail: employeeData.workEmail || employeeData.email,
        workTiming: employeeData.workTiming,
        salary: employeeData.salary,
        hiringDate: employeeData.hiringDate || employeeData.joinDate,
        dateOfBirth: employeeData.dateOfBirth || employeeData['Date of Birth'],
        emiratesIdNo: employeeData.emiratesIdNo || employeeData['Emirates ID No.'],
        emiratesIdExpiryDate: employeeData.emiratesIdExpiryDate || employeeData['Emirates ID Expiry Date'],
        residencyVisaExpiryDate: employeeData.residencyVisaExpiryDate || employeeData['Residency Visa Expiry Date'],
        residencyVisaFileNumber: employeeData.residencyVisaFileNumber || employeeData['Residency Visa File Number'],
        residencyVisaUID: employeeData.residencyVisaUID || employeeData['Residency Visa UID'],
        passportNumber: employeeData.passportNumber,
        passportExpiry: employeeData.passportExpiry,
        lineManagerName: employeeData.lineManagerName,
        lineManagerId: employeeData.lineManagerId,
        reportsTo: employeeData.reportsTo || employeeData['Reports To'],
        title: employeeData.title,
        preferredName: employeeData.preferredName,
        legalCountryOfResidence: employeeData.legalCountryOfResidence,
        probationEndDate: employeeData.probationEndDate,
        phone: employeeData.phone,
        workNo: employeeData.workNo,
        endTime: employeeData.endTime,
        startTime: employeeData.startTime,
        previousTiming: employeeData.previousTiming,
        workWeek: employeeData.workWeek,
        currentlyInsured: employeeData.currentlyInsured,
        sendInvitation: employeeData.sendInvitation,
        hasOvertime: employeeData.hasOvertime,
        overtime: employeeData.overtime,
        overtimeUpdatedAt: employeeData.overtimeUpdatedAt,
        overtimeAccessGrantedAt: employeeData.overtimeAccessGrantedAt,
        overtimeAccessRevokedAt: employeeData.overtimeAccessRevokedAt,
        pinNumber: employeeData.pinNumber,
        pinExpiry: employeeData.pinExpiry,
        pinCreatedAt: employeeData.pinCreatedAt,
        permissions: permissions,
        hasOvertimeAccess: employeeData.hasOvertimeAccess === true,
        hasOvertimeApprovalAccess: hasApprovalAccess,
        manpowerAccess: employeeData.manpowerAccess === true,
        leaveApplicationAccess: leaveApplicationAccess,
        createdAt: employeeData.createdAt,
        updatedAt: employeeData.updatedAt
      };
      
    } catch (error: any) {
      console.error('PIN login error:', error);
      throw new Error(error.message || 'PIN login failed');
    }
  }

  private isValidPin(pin: string): boolean {
    const pinRegex = /^\d{4,6}$/;
    return pinRegex.test(pin);
  }

  private async fetchUserData(uid: string, email: string): Promise<any> {
    try {
      const cacheKey = `user_${uid}`;
      const cached = this._userDataCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        return cached.data;
      }

      // Check if this is a PIN auth user
      if (email && email.includes('@ptspin.local')) {
        let employeeNumber = email.replace('@ptspin.local', '').replace('emp', '');
        if (employeeNumber.includes('_')) {
          employeeNumber = employeeNumber.split('_')[0];
        }
        
        const employeeQuery = query(
          collection(db, 'MasterSheet', 'Employee-Data', 'employees'),
          where('employeeNumber', '==', employeeNumber)
        );
        const snapshot = await getDocs(employeeQuery);
        
        if (!snapshot.empty) {
          const employeeData = snapshot.docs[0].data();
          return await this.getMasterSheetUserData(employeeData, snapshot.docs[0].id, cacheKey);
        }
      }

      // Check HR Managers collection
      const hrManagerDocRef = doc(db, 'hrManagers', uid);
      const hrManagerDoc = await getDoc(hrManagerDocRef);
      
      if (hrManagerDoc.exists()) {
        const hrManagerData = hrManagerDoc.data();
        const isHrManager = hrManagerData.role === 'hr_manager' || hrManagerData.isHrManager === true;
        const isAssistantManager = hrManagerData.role === 'assistant_manager' || hrManagerData.isAssistantManager === true;
        const isAccountingManager = hrManagerData.role === 'accounting_manager' || hrManagerData.isAccountingManager === true;
        
        let userType: AuthUser['userType'] = 'employee';
        if (isHrManager) userType = 'hr_manager';
        else if (isAssistantManager) userType = 'assistant_manager';
        else if (isAccountingManager) userType = 'accounting_manager';
        
        const permissions = [];
        if (isHrManager || isAssistantManager) {
          permissions.push('hr_leave_approval', 'hr_payroll_access', 'attendance_access', 'view_reports');
        }
        if (isAccountingManager) {
          permissions.push('finance_access', 'payroll_access', 'view_reports', 'overtime_reports', 'salary_reports');
        }
        
        const result = {
          userType: userType,
          isAdmin: false,
          isEngineer: false,
          isApprover: false,
          isHrManager: isHrManager,
          isAssistantManager: isAssistantManager,
          isAccountingManager: isAccountingManager,
          name: hrManagerData.name || hrManagerData.Name,
          designation: hrManagerData.designation || 
                      (isHrManager ? 'HR Manager' : 
                       isAssistantManager ? 'Assistant Manager' : 
                       isAccountingManager ? 'Accounting Manager' : ''),
          department: isAccountingManager ? 'Finance' : (hrManagerData.department || 'HR Department'),
          employeeNo: hrManagerData.employeeNo,
          employeeNumber: hrManagerData.employeeNumber || hrManagerData.employeeNo,
          permissions: permissions,
          hasOvertimeAccess: false,
          hasOvertimeApprovalAccess: false,
          manpowerAccess: false,
          leaveApplicationAccess: true,
          createdAt: hrManagerData.createdAt,
        };
        
        this._userDataCache.set(cacheKey, { data: result, timestamp: Date.now() });
        return result;
      }

      // Check admins collection
      const adminDocRef = doc(db, 'admins', uid);
      const adminDoc = await getDoc(adminDocRef);
      
      if (adminDoc.exists()) {
        const adminData = adminDoc.data();
        const result = {
          userType: 'admin' as const,
          isAdmin: true,
          isEngineer: false,
          isApprover: false,
          isHrManager: false,
          isAssistantManager: false,
          isAccountingManager: false,
          name: adminData.Name || adminData.name || undefined,
          designation: adminData.designation || 'Administrator',
          department: adminData.department || 'Administration',
          employeeNo: adminData.employeeNo || 'ADMIN001',
          permissions: adminData.permissions || ['all'],
          hasOvertimeAccess: true,
          hasOvertimeApprovalAccess: true,
          manpowerAccess: true,
          leaveApplicationAccess: true,
          createdAt: adminData.createdAt,
        };
        
        this._userDataCache.set(cacheKey, { data: result, timestamp: Date.now() });
        return result;
      }
      
      // Check approvers collection
      const approverDocRef = doc(db, 'approvers', uid);
      const approverDoc = await getDoc(approverDocRef);
      
      if (approverDoc.exists()) {
        const approverData = approverDoc.data();
        const hasApprovalAccess = approverData.hasOvertimeApprovalAccess === true;
        
        if (hasApprovalAccess) {
          const result = {
            userType: 'approver' as const,
            isAdmin: false,
            isEngineer: false,
            isApprover: true,
            isHrManager: false,
            isAssistantManager: false,
            isAccountingManager: false,
            name: approverData.name || 'Approver',
            designation: approverData.designation || 'Overtime Approver',
            department: approverData.department || 'Management',
            employeeNo: approverData.employeeNo || `EMP${uid.slice(-4)}`,
            employeeNumber: approverData.employeeNumber || approverData.employeeNo,
            permissions: ['overtime_approval', 'view_reports', 'manage_requests'],
            hasOvertimeAccess: true,
            hasOvertimeApprovalAccess: true,
            manpowerAccess: false,
            createdAt: approverData.createdAt,
          };
          
          this._userDataCache.set(cacheKey, { data: result, timestamp: Date.now() });
          return result;
        }
      }
      
      // Check engineers
      try {
        const engineersQuery = query(
          collection(db, 'engineers'),
          where('email', '==', email)
        );
        const engineersSnapshot = await getDocs(engineersQuery);
        
        if (!engineersSnapshot.empty) {
          const engineerData = engineersSnapshot.docs[0].data();
          const employeeNo = engineerData.employeeNo;
          const overtimePermissions = await this.getOvertimePermissions(employeeNo);
          const isApprover = overtimePermissions.hasOvertimeApprovalAccess;
          
          const result = {
            userType: isApprover ? 'approver' as const : 'engineer' as const,
            isAdmin: false,
            isEngineer: !isApprover,
            isApprover: isApprover,
            isHrManager: false,
            isAssistantManager: false,
            isAccountingManager: false,
            name: engineerData.Name || engineerData.name || undefined,
            designation: engineerData.designation || 'Engineer',
            department: engineerData.designation || 'Engineering',
            employeeNumber: engineerData.employeeNumber || employeeNo,
            employeeNo: employeeNo,
            permissions: isApprover 
              ? ['reports', 'overtime', 'overtime_approval'] 
              : ['reports', 'overtime'],
            hasOvertimeAccess: overtimePermissions.hasOvertimeAccess,
            hasOvertimeApprovalAccess: overtimePermissions.hasOvertimeApprovalAccess,
            manpowerAccess: overtimePermissions.manpowerAccess,
            leaveApplicationAccess: overtimePermissions.leaveApplicationAccess,
            createdAt: engineerData.createdAt,
          };
          
          this._userDataCache.set(cacheKey, { data: result, timestamp: Date.now() });
          return result;
        }
      } catch {}
      
      // Check MasterSheet
      try {
        const employeeDocRef = doc(db, 'MasterSheet', 'Employee-Data', 'employees', uid);
        const employeeDoc = await getDoc(employeeDocRef);
        
        if (employeeDoc.exists()) {
          return await this.getMasterSheetUserData(employeeDoc.data(), employeeDoc.id, cacheKey);
        }
      } catch {}
      
      try {
        const employeesQuery = query(
          collection(db, 'MasterSheet', 'Employee-Data', 'employees'),
          where('email', '==', email)
        );
        const employeesSnapshot = await getDocs(employeesQuery);
        
        if (!employeesSnapshot.empty) {
          const employeeDoc = employeesSnapshot.docs[0];
          return await this.getMasterSheetUserData(employeeDoc.data(), employeeDoc.id, cacheKey);
        }
      } catch {}
      
      try {
        const employeesQuery = query(
          collection(db, 'MasterSheet', 'Employee-Data', 'employees'),
          where('workEmail', '==', email)
        );
        const employeesSnapshot = await getDocs(employeesQuery);
        
        if (!employeesSnapshot.empty) {
          const employeeDoc = employeesSnapshot.docs[0];
          return await this.getMasterSheetUserData(employeeDoc.data(), employeeDoc.id, cacheKey);
        }
      } catch {}
      
      return {
        userType: 'employee',
        isAdmin: false,
        isEngineer: false,
        isApprover: false,
        isHrManager: false,
        isAssistantManager: false,
        isAccountingManager: false,
        name: email?.split('@')[0] || 'Unknown User',
        designation: 'Unknown',
        department: 'Unknown',
        permissions: [],
        hasOvertimeAccess: false,
        hasOvertimeApprovalAccess: false,
        manpowerAccess: false
      };
      
    } catch (error) {
      console.error('Error fetching user data:', error);
      return {
        userType: 'employee',
        isAdmin: false,
        isEngineer: false,
        isApprover: false,
        isHrManager: false,
        isAssistantManager: false,
        isAccountingManager: false,
        permissions: [],
        hasOvertimeAccess: false,
        hasOvertimeApprovalAccess: false,
        manpowerAccess: false
      };
    }
  }

  private async getMasterSheetUserData(employeeData: any, docId: string, cacheKey: string): Promise<any> {
    const hasApprovalAccess = employeeData.hasOvertimeApprovalAccess === true;
    
    const isHrManager = 
      employeeData.designation?.toLowerCase().includes('hr manager') || 
      employeeData.designation?.toLowerCase().includes('hr & office manager') ||
      employeeData.title?.toLowerCase().includes('hr manager') ||
      employeeData.title?.toLowerCase().includes('hr & office manager') ||
      employeeData.isHrManager === true;
    
    const isAssistantManager = 
      employeeData.designation?.toLowerCase().includes('assistant manager') ||
      employeeData.title?.toLowerCase().includes('assistant manager') ||
      employeeData.isAssistantManager === true;
    
    // FIX: Added Accounting Manager detection
    const isAccountingManager = 
      employeeData.designation?.toLowerCase().includes('accounting manager') ||
      employeeData.title?.toLowerCase().includes('accounting manager') ||
      employeeData.isAccountingManager === true;
    
    let userType: any = 'employee';
    if (isHrManager) userType = 'hr_manager';
    else if (isAssistantManager) userType = 'assistant_manager';
    else if (isAccountingManager) userType = 'accounting_manager'; // FIX: Added accounting_manager
    else if (hasApprovalAccess) userType = 'approver';

    const permissions: string[] = ['self-service'];
    
    // FIX: HR and Assistant Managers get HR permissions
    if (isHrManager || isAssistantManager) {
      permissions.push('hr_leave_approval', 'hr_payroll_access', 'attendance_access', 'view_reports', 'overtime_reports');
    }
    
    // FIX: Accounting Manager gets finance permissions
    if (isAccountingManager) {
      permissions.push('finance_access', 'payroll_access', 'view_reports', 'overtime_reports', 'salary_reports');
    }
    
    if (hasApprovalAccess) {
      permissions.push('overtime_approval');
    }
    
    // FIX: Determine leave application access for Accounting Manager
    const leaveApplicationAccess = 
      employeeData.leaveApplicationAccess === true || 
      isHrManager || 
      isAssistantManager ||
      isAccountingManager; // FIX: Accounting Managers also get leave access
    
    const result = {
      userType: userType,
      isAdmin: false,
      isEngineer: false,
      isApprover: hasApprovalAccess,
      isHrManager: isHrManager,
      isAssistantManager: isAssistantManager,
      isAccountingManager: isAccountingManager, // FIX: Added isAccountingManager
      name: employeeData.employeeName || employeeData.name || undefined,
      employeeName: employeeData.employeeName,
      firstName: employeeData.firstName || employeeData['First Name*'],
      lastName: employeeData.lastName || employeeData['Last Name*'],
      designation: employeeData.designation || employeeData.title,
      department: this.getDepartmentFromDesignation(employeeData.designation || employeeData.title),
      employeeNumber: employeeData.employeeNumber,
      employeeNo: employeeData.employeeNo || docId,
      gender: employeeData.gender || employeeData.Gender,
      nationality: employeeData.nationality || employeeData.Nationality,
      mobileNo: employeeData.mobileNo || employeeData['Mobile No.'],
      workEmail: employeeData.workEmail || employeeData.email,
      workTiming: employeeData.workTiming,
      salary: employeeData.salary,
      hiringDate: employeeData.hiringDate || employeeData.joinDate,
      dateOfBirth: employeeData.dateOfBirth || employeeData['Date of Birth'],
      emiratesIdNo: employeeData.emiratesIdNo || employeeData['Emirates ID No.'],
      emiratesIdExpiryDate: employeeData.emiratesIdExpiryDate || employeeData['Emirates ID Expiry Date'],
      residencyVisaExpiryDate: employeeData.residencyVisaExpiryDate || employeeData['Residency Visa Expiry Date'],
      residencyVisaFileNumber: employeeData.residencyVisaFileNumber || employeeData['Residency Visa File Number'],
      residencyVisaUID: employeeData.residencyVisaUID || employeeData['Residency Visa UID'],
      passportNumber: employeeData.passportNumber,
      passportExpiry: employeeData.passportExpiry,
      lineManagerName: employeeData.lineManagerName,
      lineManagerId: employeeData.lineManagerId,
      reportsTo: employeeData.reportsTo || employeeData['Reports To'],
      title: employeeData.title,
      preferredName: employeeData.preferredName,
      legalCountryOfResidence: employeeData.legalCountryOfResidence,
      probationEndDate: employeeData.probationEndDate,
      phone: employeeData.phone,
      workNo: employeeData.workNo,
      endTime: employeeData.endTime,
      startTime: employeeData.startTime,
      previousTiming: employeeData.previousTiming,
      workWeek: employeeData.workWeek,
      currentlyInsured: employeeData.currentlyInsured,
      sendInvitation: employeeData.sendInvitation,
      hasOvertime: employeeData.hasOvertime,
      overtime: employeeData.overtime,
      overtimeUpdatedAt: employeeData.overtimeUpdatedAt,
      overtimeAccessGrantedAt: employeeData.overtimeAccessGrantedAt,
      overtimeAccessRevokedAt: employeeData.overtimeAccessRevokedAt,
      pinNumber: employeeData.pinNumber,
      pinExpiry: employeeData.pinExpiry,
      pinCreatedAt: employeeData.pinCreatedAt,
      permissions: permissions,
      hasOvertimeAccess: employeeData.hasOvertimeAccess === true,
      hasOvertimeApprovalAccess: hasApprovalAccess,
      manpowerAccess: employeeData.manpowerAccess === true,
      leaveApplicationAccess: leaveApplicationAccess,
      createdAt: employeeData.createdAt,
      updatedAt: employeeData.updatedAt
    };
    
    this._userDataCache.set(cacheKey, { data: result, timestamp: Date.now() });
    return result;
  }

  private getDepartmentFromDesignation(designation: string): string {
    if (!designation) return 'General';
    const designationLower = designation.toLowerCase();
    
    if (designationLower.includes('hr') || designationLower.includes('human')) return 'Human Resources';
    if (designationLower.includes('eng')) return 'Engineering';
    if (designationLower.includes('admin')) return 'Administration';
    if (designationLower.includes('account') || designationLower.includes('finance')) return 'Finance';
    if (designationLower.includes('manager')) return 'Management';
    if (designationLower.includes('sales') || designationLower.includes('marketing')) return 'Sales & Marketing';
    if (designationLower.includes('it') || designationLower.includes('tech')) return 'IT';
    if (designationLower.includes('project')) return 'Project Management';
    if (designationLower.includes('office')) return 'Office Administration';
    
    return 'General';
  }

  private async getOvertimePermissions(employeeNo: string): Promise<{
    hasOvertimeAccess: boolean;
    hasOvertimeApprovalAccess: boolean;
    manpowerAccess: boolean;
    leaveApplicationAccess: boolean;
  }> {
    if (!employeeNo) {
      return { hasOvertimeAccess: false, hasOvertimeApprovalAccess: false, manpowerAccess: false, leaveApplicationAccess: false };
    }
    
    try {
      const masterSheetDoc = await getDoc(doc(db, 'MasterSheet', 'Employee-Data', 'employees', employeeNo));
      if (masterSheetDoc.exists()) {
        const data = masterSheetDoc.data();
        return {
          hasOvertimeAccess: data.hasOvertimeAccess === true,
          hasOvertimeApprovalAccess: data.hasOvertimeApprovalAccess === true,
          manpowerAccess: data.manpowerAccess === true,
          leaveApplicationAccess: data.leaveApplicationAccess === true
        };
      }
    } catch {}
    
    return { hasOvertimeAccess: false, hasOvertimeApprovalAccess: false, manpowerAccess: false, leaveApplicationAccess: false };
  }

  async getCurrentUserData(): Promise<AuthUser | null> {
    const user = this.getCurrentUser();
    if (!user) return null;
    const userData = await this.fetchUserData(user.uid, user.email || '');
    return this.buildAuthUser(user, userData);
  }

  async isCurrentUserAdmin(): Promise<boolean> {
    const userData = await this.getCurrentUserData();
    return userData?.isAdmin || false;
  }

  async isCurrentUserEngineer(): Promise<boolean> {
    const userData = await this.getCurrentUserData();
    return userData?.isEngineer || false;
  }

  async isCurrentUserApprover(): Promise<boolean> {
    const userData = await this.getCurrentUserData();
    return userData?.isApprover || false;
  }

  async isCurrentUserHrManager(): Promise<boolean> {
    const userData = await this.getCurrentUserData();
    return userData?.isHrManager || false;
  }

  async isCurrentUserAssistantManager(): Promise<boolean> {
    const userData = await this.getCurrentUserData();
    return userData?.isAssistantManager || false;
  }

  // FIX: Added method to check if current user is Accounting Manager
  async isCurrentUserAccountingManager(): Promise<boolean> {
    const userData = await this.getCurrentUserData();
    return userData?.isAccountingManager || false;
  }

  async isHrRole(): Promise<boolean> {
    const userData = await this.getCurrentUserData();
    return (userData?.isHrManager || userData?.isAssistantManager || userData?.isAccountingManager) || false;
  }

  async canCreateOvertimeRequests(): Promise<boolean> {
    const userData = await this.getCurrentUserData();
    return userData?.hasOvertimeAccess || false;
  }

  async canApproveOvertimeRequests(): Promise<boolean> {
    const userData = await this.getCurrentUserData();
    return userData?.hasOvertimeApprovalAccess || false;
  }

  async hasPermission(permission: string): Promise<boolean> {
    const userData = await this.getCurrentUserData();
    if (!userData?.permissions) return false;
    if (userData.permissions.includes('all')) return true;
    return userData.permissions.includes(permission);
  }

  onAuthStateChange(callback: (user: AuthUser | null) => void) {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userData = await this.fetchUserData(user.uid, user.email || '');
        callback(this.buildAuthUser(user, userData));
      } else {
        callback(null);
      }
    });
  }

  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  async getUserToken(): Promise<string | null> {
    const user = this.getCurrentUser();
    if (user) return await user.getIdToken();
    return null;
  }

  async logout(): Promise<void> {
    try {
      this._userDataCache.clear();
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) throw new Error('No authenticated user found');
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
    } catch (error: any) {
      throw new Error(this.getChangePasswordErrorMessage(error.code));
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      throw new Error(this.getResetPasswordErrorMessage(error.code));
    }
  }

  async setEmployeePin(employeeNumber: string, pin: string): Promise<void> {
    try {
      if (!this.isValidPin(pin)) throw new Error('Invalid PIN format. PIN must be 4-6 digits.');

      const employeeQuery = query(
        collection(db, 'MasterSheet', 'Employee-Data', 'employees'),
        where('employeeNumber', '==', employeeNumber)
      );
      const employeeSnapshot = await getDocs(employeeQuery);
      if (employeeSnapshot.empty) throw new Error('Employee not found');

      const employeeDoc = employeeSnapshot.docs[0];
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + this.PIN_EXPIRY_DAYS);

      await updateDoc(employeeDoc.ref, {
        pinNumber: pin,
        pinExpiry: Timestamp.fromDate(expiryDate),
        pinCreatedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        updatedBy: auth.currentUser?.email || 'System'
      });

      this.clearCacheForEmployee(employeeDoc.id);
    } catch (error: any) {
      throw new Error(`Failed to set PIN: ${error.message}`);
    }
  }

  async resetEmployeePin(employeeNumber: string): Promise<string> {
    try {
      const newPin = Math.floor(100000 + Math.random() * 900000).toString();
      await this.setEmployeePin(employeeNumber, newPin);
      return newPin;
    } catch (error: any) {
      throw new Error(`Failed to reset PIN: ${error.message}`);
    }
  }

  async getPinStatus(employeeNumber: string): Promise<{
    hasPin: boolean;
    isExpired: boolean;
    expiryDate?: Date;
    daysRemaining?: number;
  }> {
    try {
      const employeeQuery = query(
        collection(db, 'MasterSheet', 'Employee-Data', 'employees'),
        where('employeeNumber', '==', employeeNumber)
      );
      const employeeSnapshot = await getDocs(employeeQuery);
      if (employeeSnapshot.empty) throw new Error('Employee not found');

      const employeeData = employeeSnapshot.docs[0].data();
      if (!employeeData.pinNumber) return { hasPin: false, isExpired: false };

      let isExpired = false;
      let expiryDate: Date | undefined;
      let daysRemaining: number | undefined;

      if (employeeData.pinExpiry) {
        if (employeeData.pinExpiry.toDate) expiryDate = employeeData.pinExpiry.toDate();
        else if (employeeData.pinExpiry.seconds) expiryDate = new Date(employeeData.pinExpiry.seconds * 1000);
        else expiryDate = new Date(employeeData.pinExpiry);
        
        const now = new Date();
        if (expiryDate) {
          isExpired = now > expiryDate;
          if (!isExpired) {
            const diffTime = Math.abs(expiryDate.getTime() - now.getTime());
            daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          }
        }
      }

      return { hasPin: true, isExpired, expiryDate, daysRemaining };
    } catch (error: any) {
      throw new Error(`Failed to get PIN status: ${error.message}`);
    }
  }

  private buildAuthUser(user: User, userData: any): AuthUser {
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || userData.employeeName || userData.name,
      userType: userData.userType || 'employee',
      isAdmin: userData.isAdmin || false,
      isEngineer: userData.isEngineer || false,
      isApprover: userData.isApprover || false,
      isHrManager: userData.isHrManager || false,
      isAssistantManager: userData.isAssistantManager || false,
      isAccountingManager: userData.isAccountingManager || false, // FIX: Added isAccountingManager
      isPinAuth: userData.isPinAuth || user.email?.includes('@ptspin.local') || false,
      name: userData.name,
      employeeName: userData.employeeName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      designation: userData.designation,
      department: userData.department,
      employeeNumber: userData.employeeNumber,
      employeeNo: userData.employeeNo,
      gender: userData.gender,
      nationality: userData.nationality,
      mobileNo: userData.mobileNo,
      workEmail: userData.workEmail,
      workTiming: userData.workTiming,
      salary: userData.salary,
      hiringDate: userData.hiringDate,
      dateOfBirth: userData.dateOfBirth,
      emiratesIdNo: userData.emiratesIdNo,
      emiratesIdExpiryDate: userData.emiratesIdExpiryDate,
      residencyVisaExpiryDate: userData.residencyVisaExpiryDate,
      residencyVisaFileNumber: userData.residencyVisaFileNumber,
      residencyVisaUID: userData.residencyVisaUID,
      passportNumber: userData.passportNumber,
      passportExpiry: userData.passportExpiry,
      lineManagerName: userData.lineManagerName,
      lineManagerId: userData.lineManagerId,
      reportsTo: userData.reportsTo,
      title: userData.title,
      preferredName: userData.preferredName,
      legalCountryOfResidence: userData.legalCountryOfResidence,
      probationEndDate: userData.probationEndDate,
      phone: userData.phone,
      workNo: userData.workNo,
      endTime: userData.endTime,
      startTime: userData.startTime,
      previousTiming: userData.previousTiming,
      workWeek: userData.workWeek,
      currentlyInsured: userData.currentlyInsured,
      sendInvitation: userData.sendInvitation,
      hasOvertime: userData.hasOvertime,
      overtime: userData.overtime,
      overtimeUpdatedAt: userData.overtimeUpdatedAt,
      overtimeAccessGrantedAt: userData.overtimeAccessGrantedAt,
      overtimeAccessRevokedAt: userData.overtimeAccessRevokedAt,
      pinNumber: userData.pinNumber,
      pinExpiry: userData.pinExpiry,
      pinCreatedAt: userData.pinCreatedAt,
      permissions: userData.permissions || [],
      hasOvertimeAccess: userData.hasOvertimeAccess || false,
      hasOvertimeApprovalAccess: userData.hasOvertimeApprovalAccess || false,
      manpowerAccess: userData.manpowerAccess || false,
      leaveApplicationAccess: userData.leaveApplicationAccess || false,
      createdAt: userData.createdAt,
      updatedAt: userData.updatedAt
    };
  }

  private getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/user-not-found': return 'No user found with this email address.';
      case 'auth/wrong-password': return 'Incorrect password. Please try again.';
      case 'auth/invalid-email': return 'Invalid email address format.';
      case 'auth/user-disabled': return 'This account has been disabled.';
      case 'auth/too-many-requests': return 'Too many failed attempts. Please try again later.';
      case 'auth/invalid-credential': return 'Invalid credentials. Please check your email and password.';
      case 'auth/email-already-in-use': return 'Email address is already in use.';
      case 'auth/weak-password': return 'Password is too weak. Please choose a stronger password.';
      case 'auth/operation-not-allowed': return 'This operation is not allowed.';
      case 'auth/network-request-failed': return 'Network error. Please check your connection.';
      default: return 'Login failed. Please check your credentials and try again.';
    }
  }

  private getChangePasswordErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/wrong-password': return 'Current password is incorrect.';
      case 'auth/weak-password': return 'New password is too weak. Please choose a stronger password.';
      case 'auth/requires-recent-login': return 'Please log out and log in again before changing your password.';
      case 'auth/invalid-credential': return 'Current password is incorrect.';
      case 'auth/too-many-requests': return 'Too many attempts. Please try again later.';
      case 'auth/network-request-failed': return 'Network error. Please check your connection and try again.';
      default: return 'Failed to change password. Please try again.';
    }
  }

  private getResetPasswordErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/user-not-found': return 'No user found with this email address.';
      case 'auth/invalid-email': return 'Invalid email address format.';
      case 'auth/too-many-requests': return 'Too many attempts. Please try again later.';
      case 'auth/network-request-failed': return 'Network error. Please check your connection.';
      default: return 'Failed to send password reset email. Please try again.';
    }
  }

  clearCache(): void {
    this._userDataCache.clear();
  }

  clearCacheForEmployee(employeeId: string): void {
    const cacheKey = `user_${employeeId}`;
    this._userDataCache.delete(cacheKey);
  }
}

export const authService = new AuthService();